<template>
  <section class="wallet-legacy section-card">
    <header class="section-card__header">
      <div>
        <h2 class="section-card__title">
          <i class="fa-regular fa-wallet section-card__icon" />
          {{ t('billing.subscriptionPage.walletLegacy.title') }}
        </h2>
        <p class="section-card__subtitle">
          {{ t('billing.subscriptionPage.walletLegacy.subtitle') }}
        </p>
      </div>
      <Button
        :label="t('billing.subscriptionPage.walletLegacy.actions.buy')"
        icon="fa-regular fa-plus"
        class="p-button p-button-sm wallet-legacy__primary"
        @click="$emit('buy-credits')"
      />
    </header>

    <div class="wallet-legacy__grid">
      <!-- Newsletter — subscription allowance + one-shot credits -->
      <div class="wallet-legacy__stat">
        <div class="wallet-legacy__stat-head">
          <span class="wallet-legacy__stat-icon wallet-legacy__stat-icon--nl">
            <i class="fa-regular fa-envelope" />
          </span>
          <span class="wallet-legacy__stat-name">
            {{ t('billing.subscriptionPage.walletLegacy.newsletter') }}
          </span>
        </div>
        <div class="wallet-legacy__stat-value">
          {{ formatNumber(walletLegacy.newsletter.totalRemaining) }}
        </div>
        <div class="wallet-legacy__stat-unit">
          {{ t('billing.subscriptionPage.walletLegacy.emailsRemaining') }}
        </div>
        <div class="wallet-legacy__breakdown">
          <div class="wallet-legacy__pill">
            <span class="wallet-legacy__pill-label">
              {{ t('billing.subscriptionPage.walletLegacy.fromSubscription') }}
            </span>
            <span class="wallet-legacy__pill-value">
              {{ formatNumber(walletLegacy.newsletter.subscriptionRemaining) }}
            </span>
          </div>
          <div class="wallet-legacy__pill">
            <span class="wallet-legacy__pill-label">
              {{ t('billing.subscriptionPage.walletLegacy.fromCredits') }}
            </span>
            <span class="wallet-legacy__pill-value">
              {{ formatNumber(walletLegacy.newsletter.oneShotRemaining) }}
            </span>
          </div>
        </div>
      </div>

      <!-- SMS — one-shot credits only (V3 bundles include no SMS) -->
      <div class="wallet-legacy__stat">
        <div class="wallet-legacy__stat-head">
          <span class="wallet-legacy__stat-icon wallet-legacy__stat-icon--sms">
            <i class="fa-regular fa-comment" />
          </span>
          <span class="wallet-legacy__stat-name">
            {{ t('billing.subscriptionPage.walletLegacy.sms') }}
          </span>
        </div>
        <div class="wallet-legacy__stat-value">
          {{ formatNumber(walletLegacy.sms.oneShotRemaining) }}
        </div>
        <div class="wallet-legacy__stat-unit">
          {{ t('billing.subscriptionPage.walletLegacy.smsRemaining') }}
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import { WalletLegacySection } from '@/types/billing-types';

export default defineComponent({
  name: 'WalletLegacyPanel',

  components: { Button },

  props: {
    walletLegacy: {
      type: Object as PropType<WalletLegacySection>,
      required: true,
    },
  },

  emits: ['buy-credits'],

  setup() {
    const { t } = useI18n();
    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);
    return { t, formatNumber };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.wallet-legacy {
  &__primary {
    background: $brand-color-primary !important;
    border-color: $brand-color-primary !important;
    color: #fff !important;
    font-weight: 600 !important;

    &:hover {
      background: #7aae26 !important;
      border-color: #7aae26 !important;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 16px;

    @media (max-width: 560px) {
      grid-template-columns: 1fr;
    }
  }

  &__stat {
    background: #fbfaf7;
    border: 1px solid #eaeaea;
    border-radius: 12px;
    padding: 18px;
  }

  &__stat-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  &__stat-icon {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 0.85rem;

    &--nl {
      background: rgba(148, 200, 64, 0.14);
      color: #629d03;
    }
    &--sms {
      background: rgba(96, 165, 250, 0.16);
      color: #2563eb;
    }
  }

  &__stat-name {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: -0.02em;
    line-height: 1.1;
  }

  &__stat-unit {
    font-size: 0.8125rem;
    color: #9ca3af;
    margin-top: 2px;
  }

  &__breakdown {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 100px;
    font-size: 0.8125rem;
    background: #fff;
    border: 1px solid #e5e7eb;
  }

  &__pill-label {
    color: #6b7280;
    font-weight: 500;
  }

  &__pill-value {
    color: #1f2937;
    font-weight: 600;
  }
}
</style>
